import { Edit, Package, Plus, Shirt, Trash2, RefreshCw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import { Button } from '../../app/components';
import MainHeader from '../Common/MainHeader/MainHeader';
import { auth } from '../../firebase/config';
import { useConfirmModal } from '../../hooks/useConfirmModal';
import { addProduto, deleteProduto, getProdutos, updateProduto } from '../../services/firestoreService';
import { Produto, ProdutoFormData } from '../../types';
import ConfirmModal from '../Common/ConfirmModal';
import { Pagination } from '../Common/Pagination';
import ProdutoForm from './ProdutoForm';
import './ProdutosList.css';

const ProdutosList: React.FC = () => {
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingProduto, setEditingProduto] = useState<Produto | null>(null);
  const [userId, setUserId] = useState<string>('');
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(20);
  
  const { modalState, showConfirm, hideConfirm, handleConfirm } = useConfirmModal();

  // Estado temporário para input (melhora performance com debounce)
  const [filtrosInput, setFiltrosInput] = useState({
    codigo: '',
    titulo: '',
    tamanho: '',
    cor: ''
  });

  // Estado efetivo dos filtros (usado na filtragem)
  const [filtros, setFiltros] = useState({
    codigo: '',
    titulo: '',
    tamanho: '',
    cor: ''
  });

  // Aguarda a autenticação do Firebase
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
      }
    });
    return () => unsubscribe();
  }, []);

  // Carrega todos os produtos quando userId está disponível
  // Implementa cache em memória para reduzir leituras do Firestore
  const loadProdutos = async (forceRefresh: boolean = false) => {
    if (!userId) return;
    
    // Verifica se já tem dados em cache e não é refresh forçado
    const cacheKey = `produtos_${userId}`;
    const cacheTimeKey = `produtos_${userId}_time`;
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos em ms

    if (!forceRefresh) {
      const cachedData = sessionStorage.getItem(cacheKey);
      const cachedTime = sessionStorage.getItem(cacheTimeKey);
      
      if (cachedData && cachedTime) {
        const age = Date.now() - parseInt(cachedTime);
        if (age < CACHE_DURATION) {
          // Usando cache - economiza leituras do Firestore
          const produtosCached = JSON.parse(cachedData);
          setProdutos(produtosCached);
          setLoading(false);
          return;
        }
      }
    }
    
    // Se é refresh, usa estado diferente para não mostrar tela de loading
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await getProdutos(userId);
      if (result.success && result.data) {
        setProdutos(result.data);
        
        // Salva no cache
        sessionStorage.setItem(cacheKey, JSON.stringify(result.data));
        sessionStorage.setItem(cacheTimeKey, Date.now().toString());
      } else {
        setError(result.error || 'Erro ao carregar produtos');
      }
    } catch (err) {
      setError('Erro ao carregar produtos');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (userId) {
      loadProdutos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleClearFilters = () => {
    setFiltrosInput({
      codigo: '',
      titulo: '',
      tamanho: '',
      cor: ''
    });
    setFiltros({
      codigo: '',
      titulo: '',
      tamanho: '',
      cor: ''
    });
    setCurrentPage(1);
  };

  // Debounce dos filtros - aguarda 500ms após parar de digitar para filtrar
  // Isso melhora a performance ao evitar re-renders excessivos durante a digitação
  useEffect(() => {
    const timer = setTimeout(() => {
      setFiltros(filtrosInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [filtrosInput]);

  const handleDelete = async (id: string) => {
    const produto = produtos.find(p => p.id === id);
    showConfirm(
      'Excluir Produto',
      `Tem certeza que deseja excluir o produto ${produto?.titulo || 'não identificado'}? Esta ação não pode ser desfeita.`,
      async () => {
        const result = await deleteProduto(id);
        if (result.success) {
          await loadProdutos(true); // força refresh do Firestore
        } else {
          alert(result.error || 'Erro ao excluir produto');
        }
      },
      'danger',
      'Sim, Excluir',
      'Cancelar'
    );
  };

  const handleEdit = (produto: Produto) => {
    setEditingProduto(produto);
    setShowForm(true);
  };

  const handleSave = async (produtoData: ProdutoFormData) => {
    try {
      const uid = auth.currentUser?.uid!;
      let result;
      if (editingProduto) {
        result = await updateProduto(editingProduto.id!, { ...produtoData, userId: uid });
      } else {
        result = await addProduto({ ...produtoData, userId: uid } as Omit<Produto, 'id' | 'createdAt'>);
      }

      if (result.success) {
        await loadProdutos(true); // força refresh do Firestore
      } else {
        throw new Error(result.error || 'Erro ao salvar produto');
      }
    } catch (error: any) {
      throw error;
    }
  };

  // Filtros client-side
  const produtosFiltrados = produtos.filter(produto => {
    let match = true;
    
    if (filtros.codigo) {
      const codigo = (produto.codigoProduto || '').toLowerCase();
      match = match && codigo.includes(filtros.codigo.toLowerCase());
    }
    
    if (filtros.titulo) {
      const titulo = (produto.titulo || '').toLowerCase();
      match = match && titulo.includes(filtros.titulo.toLowerCase());
    }
    
    if (filtros.tamanho) {
      const tamanho = (produto.tamanho || '').toLowerCase();
      match = match && tamanho.includes(filtros.tamanho.toLowerCase());
    }
    
    if (filtros.cor) {
      const cor = (produto.cor || '').toLowerCase();
      match = match && cor.includes(filtros.cor.toLowerCase());
    }
    
    return match;
  });

  // Paginação client-side sobre os dados filtrados
  const totalPages = Math.ceil(produtosFiltrados.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const produtosPaginados = produtosFiltrados.slice(startIndex, endIndex);

  // Reset página ao mudar filtros ou itens por página
  useEffect(() => {
    setCurrentPage(1);
  }, [filtros, itemsPerPage]);

  if (loading || !userId) {
    return (
      <Container fluid className="p-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="p-1">
      {/* Header com estatísticas */}
      <MainHeader
        title="Gestão de Produtos"
        subtitle={`Total de ${produtos.length} produto${produtos.length !== 1 ? 's' : ''} cadastrado${produtos.length !== 1 ? 's' : ''}`}
        icon={<Shirt size={32} />}
      >
        <Button
          btnColor="success"
          onClick={() => {
            setEditingProduto(null);
            setShowForm(true);
          }}
          className="modern-btn d-flex align-items-center gap-2"
          style={{ borderRadius: '12px', padding: '12px 24px' }}
        >
          <Plus size={20} />
          Novo Produto
        </Button>
      </MainHeader>

      {/* Card de busca e filtros */}

      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
        <Card.Body className="p-4" style={{ background: '#F9FAFB' }}>
          <div className="align-items-center">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center gap-3">
                <h3 className="mb-0">Filtros</h3>
              </div>
            </div>
            <div className="filters-container">
              <Form.Group className="form-group">
                <Form.Label className="fw-semibold text-dark mb-2">Código</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite o código do produto"
                  value={filtrosInput.codigo}
                  onChange={(e) => setFiltrosInput({ ...filtrosInput, codigo: e.target.value })}
                  style={{ borderRadius: '12px', border: '2px solid #e9ecef', padding: '12px 16px' }}
                />
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label className="fw-semibold text-dark mb-2">Produto/Marca</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite o produto/marca do produto"
                  value={filtrosInput.titulo}
                  onChange={(e) => setFiltrosInput({ ...filtrosInput, titulo: e.target.value })}
                  style={{ borderRadius: '12px', border: '2px solid #e9ecef', padding: '12px 16px' }}
                />
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label className="fw-semibold text-dark mb-2">Tamanho</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite o tamanho"
                  value={filtrosInput.tamanho}
                  onChange={(e) => setFiltrosInput({ ...filtrosInput, tamanho: e.target.value })}
                  style={{ borderRadius: '12px', border: '2px solid #e9ecef', padding: '12px 16px' }}
                />
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label className="fw-semibold text-dark mb-2">Cor</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite a cor"
                  value={filtrosInput.cor}
                  onChange={(e) => setFiltrosInput({ ...filtrosInput, cor: e.target.value })}
                  style={{ borderRadius: '12px', border: '2px solid #e9ecef', padding: '12px 16px' }}
                />
              </Form.Group>
            </div>
            <div className="filter-actions mt-3 d-flex justify-content-end ">
              {/* <div>
                <Badge bg="info" className="px-3 py-2" style={{ borderRadius: '8px', fontSize: '14px' }}>
                  {produtosFiltrados.length} resultado{produtosFiltrados.length !== 1 ? 's' : ''}
                </Badge>
              </div> */}
              <Button
                btnColor="warning"
                onClick={handleClearFilters}
                style={{ borderRadius: '12px', padding: '12px 24px', color: 'white' }}
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>



      {/* Lista de produtos - Desktop: Tabela, Mobile: Cards */}
      <Row>
        <Col>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

          {produtosFiltrados.length === 0 ? (
            <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
              <Card.Body className="text-center text-muted py-5">
                <div className="d-flex flex-column align-items-center gap-3">
                  <Shirt size={48} className="text-muted" />
                  <div>
                    <h5 className="text-muted mb-2">
                      {filtrosInput.codigo || filtrosInput.titulo || filtrosInput.tamanho || filtrosInput.cor ? 'Nenhum produto encontrado' : 'Nenhum produto cadastrado'}
                    </h5>
                    <p className="text-muted mb-0">
                      {filtrosInput.codigo || filtrosInput.titulo || filtrosInput.tamanho || filtrosInput.cor ? 'Tente ajustar os termos de busca' : 'Comece adicionando seu primeiro produto'}
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <>
              {/* Layout Desktop - Tabela */}
              <div className="d-none d-lg-block">
                <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                  <Card.Body className="p-0">
                    <Table responsive hover className="mb-0 modern-table">
                      <thead className="table-light text-dark">
                        <tr>
                          <th>Código</th>
                          <th >Produto</th>
                          <th >Tamanho</th>
                          <th >Cor</th>
                          {/* <th >Quantidade</th> */}
                          <th >Descrição</th>
                          <th className='text-center' style={{ width: '150px' }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {produtosPaginados.map((produto) => (
                          <tr key={produto.id} className="modern-table-row">
                            <td className="py-3 px-4">
                              {produto.codigoProduto ? (
                                <span className='fw-bold text-primary'>
                                  {produto.codigoProduto}
                                </span>
                              ) : (
                                <small className="text-muted">-</small>
                              )}
                            </td>
                            <td className="align-middle">
                              <div className="fw-bold text-dark fs-6">{produto.titulo}</div>
                              <div className="text-muted small">
                                <span className="fw-semibold text-muted">Qtd:</span> {produto.quantidade}
                              </div>
                            </td>

                            <td >{produto.tamanho}</td>
                            <td >{produto.cor}</td>
                            {/* <td className="py-3 px-4">
                              <span className={'fw-bold text-dark'}>
                                {produto.quantidade}
                              </span>
                            </td> */}
                            <td className="py-3 px-4">
                              <div className="text-truncate text-dark" style={{ maxWidth: '200px' }}>
                                {produto.descricao || '-'}
                              </div>
                            </td>
                            <td className="text-center">
                              <ButtonGroup size="sm">
                                <Button
                                  btnColor="primary"
                                  onClick={() => handleEdit(produto)}
                                  style={{ borderRadius: '8px', padding: '6px 10px' }}
                                >
                                  <Edit size={16}/>
                                </Button>

                                <Button
                                  btnColor="danger"
                                  onClick={() => handleDelete(produto.id!)}
                                  style={{ borderRadius: '8px', padding: '6px 10px' }}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </ButtonGroup>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </div>

              {/* Layout Mobile/Tablet - Cards */}
              <div className="d-lg-none">
                <Row className="g-3">
                  {produtosPaginados.map((produto: Produto) => (
                    <Col xs={12} key={produto.id}>
                      <Card className="border-0 shadow-sm modern-card" style={{ borderRadius: '16px' }}>
                        <Card.Body className="p-4">
                          <Row className="align-items-center">
                            <Col xs={8}>
                              <div className="d-flex align-items-center gap-3 mb-2">
                                <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                  <Package size={20} className="text-primary" />
                                </div>
                                <div>
                                  <h6 className="mb-1 fw-bold text-dark">{produto.titulo}</h6>
                                  <div className="d-flex gap-2 flex-wrap">
                                    {produto.codigoProduto && (
                                      <Badge bg="dark" style={{ borderRadius: '6px', fontSize: '11px', fontFamily: 'monospace' }}>
                                        {produto.codigoProduto}
                                      </Badge>
                                    )}
                                    <Badge bg="secondary" style={{ borderRadius: '6px', fontSize: '11px' }}>{produto.tamanho}</Badge>
                                    <Badge bg="info" style={{ borderRadius: '6px', fontSize: '11px' }}>{produto.cor}</Badge>
                                  </div>
                                </div>
                              </div>

                              <div className="row g-2">
                                <Col xs={6}>
                                  <div>
                                    <small className="text-muted d-block">Quantidade</small>
                                    <span className="text-dark fw-bold">{produto.quantidade}</span>
                                  </div>
                                </Col>
                                <Col xs={6}>
                                  <div>
                                    <small className="text-muted d-block">Status</small>
                                    <Badge bg="success" style={{ borderRadius: '6px', fontSize: '11px' }}>
                                      {produto.quantidade > 0 ? 'Disponível' : 'Indisponível'}
                                    </Badge>
                                  </div>
                                </Col>
                              </div>

                              {produto.descricao && (
                                <div className="mt-2">
                                  <small className="text-muted d-block">Descrição</small>
                                  <small className="text-dark">{produto.descricao}</small>
                                </div>
                              )}
                            </Col>

                            <Col xs={4} className="text-end">
                              <div className="d-flex flex-column gap-2">
                                <Button
                                  btnColor="primary"
                                  onClick={() => handleEdit(produto)}
                                  style={{ borderRadius: '8px', padding: '8px' }}
                                >
                                  <Edit size={16} />
                                </Button>
                                <Button
                                  btnColor="outline-danger"
                                  onClick={() => handleDelete(produto.id!)}
                                  style={{ borderRadius: '8px', padding: '8px' }}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Componente de Paginação */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={produtosFiltrados.length}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onItemsPerPageChange={setItemsPerPage}
                showItemsPerPage={true}
                itemsPerPageOptions={[5, 10, 20, 50]}
                className="mt-3"
              />
            </>
          )}
        </Col>
      </Row>

      <ProdutoForm
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setEditingProduto(null);
        }}
        onSave={handleSave}
        produto={editingProduto}
      />

      <ConfirmModal
        show={modalState.show}
        onHide={hideConfirm}
        onConfirm={handleConfirm}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
        loading={modalState.loading}
      />
    </Container>
  );
};

export default ProdutosList;
